import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { ConsoleConnection, ConsoleStoreSummary } from './console-api.js';
import type { OnboardingStore } from './onboarding-storage.js';
import { emptyDraft, type OnboardingDraft, type PatchableSection } from './onboarding-state.js';

export type LoadStatus = 'carregando' | 'pronto' | 'erro';

export interface OnboardingContextValue {
  draft: OnboardingDraft;
  connection: ConsoleConnection | null;
  store: ConsoleStoreSummary | null;
  loadStatus: LoadStatus;
  saveError: string | null;
  patchDraft: <K extends PatchableSection>(
    section: K,
    patch: Partial<OnboardingDraft[K]>,
  ) => void;
  setConnection: (connection: ConsoleConnection | null) => void;
  flush: () => Promise<void>;
  reload: () => void;
}

interface Snapshot {
  userId: string | null;
  draft: OnboardingDraft;
  connection: ConsoleConnection | null;
  store: ConsoleStoreSummary | null;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export interface OnboardingProviderProps {
  store: OnboardingStore | null;
  userId: string | null;
  children: ReactNode;
}

export function OnboardingProvider({ store, userId, children }: OnboardingProviderProps) {
  // One state object, so the user and their draft can never be out of sync — the bug that lets
  // one merchant briefly see another's answers after a sign-in switch.
  const [snapshot, setSnapshot] = useState<Snapshot>({
    userId: null,
    draft: emptyDraft(),
    connection: null,
    store: null,
  });
  const [loadStatus, setLoadStatus] = useState<LoadStatus>('carregando');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;

  useEffect(() => {
    if (!store || !userId) {
      setSnapshot({ userId: null, draft: emptyDraft(), connection: null, store: null });
      setLoadStatus('carregando');
      setSaveError(null);

      return;
    }

    let active = true;
    setLoadStatus('carregando');
    setSaveError(null);

    store
      .load()
      .then((loaded) => {
        if (!active) {
          return;
        }

        setSnapshot({ userId, draft: loaded.draft, connection: loaded.connection, store: loaded.store });
        setLoadStatus('pronto');
      })
      .catch(() => {
        if (!active) {
          return;
        }

        setLoadStatus('erro');
      });

    return () => {
      active = false;
    };
  }, [store, userId, reloadToken]);

  useEffect(() => {
    if (!store?.subscribeToErrors) {
      return;
    }

    return store.subscribeToErrors((error) => {
      setSaveError(saveErrorMessage(error));
    });
  }, [store]);

  // Flush on the way out. Without this, the last edit before closing the tab is lost inside the
  // debounce window.
  useEffect(() => {
    if (!store) {
      return;
    }

    function handleAway() {
      void store?.flush();
    }

    window.addEventListener('pagehide', handleAway);
    document.addEventListener('visibilitychange', handleAway);

    return () => {
      window.removeEventListener('pagehide', handleAway);
      document.removeEventListener('visibilitychange', handleAway);
    };
  }, [store]);

  const patchDraft = useCallback<OnboardingContextValue['patchDraft']>(
    (section, patch) => {
      const current = snapshotRef.current;

      if (!current.userId || !store) {
        return;
      }

      const draft: OnboardingDraft = {
        ...current.draft,
        updatedAt: new Date().toISOString(),
        [section]: { ...current.draft[section], ...patch },
      };

      setSnapshot({ ...current, draft });
      setSaveError(null);
      store.scheduleSave(draft);
    },
    [store],
  );

  const setConnection = useCallback((connection: ConsoleConnection | null) => {
    setSnapshot((current) => ({ ...current, connection }));
  }, []);

  const flush = useCallback(async () => {
    if (!store) {
      return;
    }

    await store.flush();
  }, [store]);

  const reload = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const value = useMemo<OnboardingContextValue>(
    () => ({
      draft: snapshot.draft,
      connection: snapshot.connection,
      store: snapshot.store,
      loadStatus: effectiveLoadStatus(loadStatus, snapshot.userId, userId),
      saveError,
      patchDraft,
      setConnection,
      flush,
      reload,
    }),
    [snapshot, loadStatus, userId, saveError, patchDraft, setConnection, flush, reload],
  );

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

/**
 * 'pronto' only once the loaded user matches the session user. Rendering a form before that
 * flashes empty inputs and drops any keystroke typed in the gap.
 */
function saveErrorMessage(error: unknown): string {
  // The technical cause goes to the browser log, never to the lojista.
  console.error('[console] falha ao salvar as respostas', error);

  return 'Não conseguimos salvar suas respostas. Verifique sua conexão e tente novamente.';
}

function effectiveLoadStatus(
  status: LoadStatus,
  loadedUserId: string | null,
  sessionUserId: string | null,
): LoadStatus {
  if (status !== 'pronto') {
    return status;
  }

  if (loadedUserId !== sessionUserId) {
    return 'carregando';
  }

  return 'pronto';
}

export function useOnboarding(): OnboardingContextValue {
  const value = useContext(OnboardingContext);

  if (!value) {
    throw new Error('useOnboarding must be used inside an OnboardingProvider.');
  }

  return value;
}
